// The atmosphere is decorative; only registered concepts are selectable.
// Every coloured cloud follows the visible concepts in the active layer.
const VERTEX = `
precision highp float;
attribute vec3 a_position;
attribute vec3 a_colour;
attribute vec4 a_style;
uniform vec2 u_resolution;
uniform vec2 u_origin;
uniform vec2 u_axisX;
uniform vec2 u_axisY;
uniform vec2 u_pointer;
uniform float u_time;
uniform float u_dpr;
uniform float u_scale;
varying vec3 v_colour;
varying float v_alpha;
varying float v_soft;
void main() {
  vec3 p = a_position;
  float phase = a_style.w;
  p.xy += vec2(sin(u_time * .025 + phase), cos(u_time * .019 + phase * 1.3)) * .0017;
  vec2 screen = u_origin + u_axisX * p.x + u_axisY * p.y;
  screen += u_pointer * p.z * 15.0;
  gl_Position = vec4(screen.x / u_resolution.x * 2.0 - 1.0, 1.0 - screen.y / u_resolution.y * 2.0, 0.0, 1.0);
  gl_PointSize = clamp(a_style.x * u_scale * u_dpr * (1.0 + p.z * .2), 1.0, 26.0 * u_dpr);
  v_colour = a_colour;
  v_alpha = a_style.y * (.88 + .12 * sin(u_time * .15 + phase));
  v_soft = a_style.z;
}`;

const FRAGMENT = `
precision mediump float;
varying vec3 v_colour;
varying float v_alpha;
varying float v_soft;
void main() {
  float d = length(gl_PointCoord - .5) * 2.0;
  if (d > 1.0) discard;
  float halo = exp(-d * d * mix(5.0, 3.0, v_soft)) * (1.0 - smoothstep(.6, 1.0, d));
  float core = exp(-d * d * 36.0) * (1.0 - v_soft) * .6;
  float alpha = (halo + core) * v_alpha;
  gl_FragColor = vec4((v_colour + vec3(core * .22)) * alpha, alpha);
}`;

const QUAD_VERTEX = `
attribute vec2 a_quad;
varying vec2 v_uv;
void main(){v_uv=a_quad*.5+.5;gl_Position=vec4(a_quad,0.0,1.0);}`;

const VOLUME_FRAGMENT = `
precision highp float;
varying vec2 v_uv;
uniform vec2 u_resolution;
uniform vec2 u_origin;
uniform mat2 u_inverse;
uniform vec2 u_pointer;
uniform vec3 u_disc;
uniform vec4 u_clouds[12];
uniform vec3 u_colours[12];
uniform float u_time;
uniform float u_focus;
float hash(vec3 p){p=fract(p*.3183099+vec3(.17,.31,.23));p*=17.0;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}
float noise(vec3 p){
  vec3 i=floor(p),f=fract(p);f=f*f*(3.0-2.0*f);
  return mix(mix(mix(hash(i),hash(i+vec3(1,0,0)),f.x),mix(hash(i+vec3(0,1,0)),hash(i+vec3(1,1,0)),f.x),f.y),
             mix(mix(hash(i+vec3(0,0,1)),hash(i+vec3(1,0,1)),f.x),mix(hash(i+vec3(0,1,1)),hash(i+vec3(1,1,1)),f.x),f.y),f.z);
}
float fbm(vec3 p){return noise(p)*.47+noise(p*2.03+vec3(3.1,1.7,2.4))*.28+noise(p*4.11+vec3(8.3,4.2,1.8))*.16+noise(p*8.17+vec3(3.2,7.4,4.6))*.09;}
void main(){
  vec2 screen=vec2(v_uv.x,1.0-v_uv.y)*u_resolution;
  vec2 world=u_inverse*(screen-u_origin);
  vec2 g=(world-u_disc.xy)/u_disc.z;
  g=mat2(.94,.34,-.34,.94)*g;
  g.y/= .60;
  float radius=length(g);
  float spiral=pow(.5+.5*sin(atan(g.y,g.x)*4.0+radius*6.8),3.0);
  float mass=exp(-radius*radius*.8)*(.055+spiral*.30)*u_focus;
  vec3 colour=vec3(.21,.28,.48)*mass;
  for(int i=0;i<12;i++){
    vec2 offset=(world-u_clouds[i].xy)/max(.01,u_clouds[i].z);
    float d=dot(offset,offset);
    float cloud=exp(-d*1.9)*u_clouds[i].w*.75;
    mass+=cloud;
    colour+=u_colours[i]*cloud;
  }
  colour/=max(mass,.0001);
  if(mass<.003){gl_FragColor=vec4(0);return;}
  vec3 light=vec3(0);
  float transmittance=1.0;
  // Integrate a thin 3D cloud volume. The slow depth motion never moves a record.
  for(int step=0;step<14;step++){
    float z=-1.0+float(step)/6.5;
    vec2 ray=world+u_pointer*z*.004;
    vec3 p=vec3(ray*18.0,z*1.5+u_time*.013);
    float warp=noise(p*.65+vec3(1.8,5.2,2.6));
    float texture=fbm(p+vec3(warp*.8,warp*.6,0));
    float density=mass*exp(-z*z*3.6)*smoothstep(.38,.72,texture)*.21;
    float shade=.34+texture*.95+max(0.0,z)*.16;
    vec3 scattering=colour*shade+vec3(.08,.10,.18)*texture;
    light+=scattering*density*transmittance;
    transmittance*=exp(-density*1.25);
  }
  float alpha=1.0-transmittance;
  gl_FragColor=vec4(light,alpha);
}`;

const COPY_FRAGMENT = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_image;
void main(){gl_FragColor=texture2D(u_image,v_uv);}`;

function seeded(value) {
  let seed = 2166136261;
  for (const c of String(value)) seed = Math.imul(seed ^ c.charCodeAt(0), 16777619);
  return () => {
    seed += 0x6D2B79F5;
    let t = seed;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

function normal(random) {
  return Math.sqrt(-2 * Math.log(Math.max(.00001, random()))) * Math.cos(random() * Math.PI * 2);
}

function rgb(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255];
}

function shader(gl, type, source) {
  const result = gl.createShader(type);
  gl.shaderSource(result, source);
  gl.compileShader(result);
  if (!gl.getShaderParameter(result, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(result);
    gl.deleteShader(result);
    throw new Error(message || 'Particle shader could not compile.');
  }
  return result;
}

function quadProgram(gl, fragment) {
  const vs=shader(gl,gl.VERTEX_SHADER,QUAD_VERTEX);
  const fs=shader(gl,gl.FRAGMENT_SHADER,fragment);
  const program=gl.createProgram();
  gl.attachShader(program,vs);gl.attachShader(program,fs);gl.linkProgram(program);
  gl.deleteShader(vs);gl.deleteShader(fs);
  if(!gl.getProgramParameter(program,gl.LINK_STATUS))throw new Error('Cloud volume could not link.');
  return program;
}

export class ParticleField {
  constructor(canvas) {
    this.canvas = canvas;
    this.points = [];
    this.groups = [];
    this.mode = 'canvas';
    this.quality = 1;
    this.lost = false;
    this.gl = canvas.getContext('webgl', { alpha: true, antialias: false, depth: false, premultipliedAlpha: true, powerPreference: 'low-power' });
    if (this.gl) {
      const gl = this.gl;
      try {
        const vs = shader(gl, gl.VERTEX_SHADER, VERTEX);
        const fs = shader(gl, gl.FRAGMENT_SHADER, FRAGMENT);
        const program = gl.createProgram();
        gl.attachShader(program, vs);
        gl.attachShader(program, fs);
        gl.linkProgram(program);
        gl.deleteShader(vs);
        gl.deleteShader(fs);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Particle shader could not link.');
        this.program = program;
        this.buffer = gl.createBuffer();
        this.uniforms = Object.fromEntries(['resolution','origin','axisX','axisY','pointer','time','dpr','scale'].map(key => [key, gl.getUniformLocation(program, `u_${key}`)]));
        this.attributes = ['position','colour','style'].map(key => gl.getAttribLocation(program, `a_${key}`));
        this.volumeProgram=quadProgram(gl,VOLUME_FRAGMENT);
        this.copyProgram=quadProgram(gl,COPY_FRAGMENT);
        this.volumeUniforms=Object.fromEntries(['resolution','origin','inverse','pointer','disc','clouds[0]','colours[0]','time','focus'].map(key=>[key,gl.getUniformLocation(this.volumeProgram,`u_${key}`)]));
        this.quad=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,this.quad);
        gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,1,1]),gl.STATIC_DRAW);
        this.volumeTexture=gl.createTexture();this.framebuffer=gl.createFramebuffer();
        this.mode = 'webgl';
        canvas.addEventListener('webglcontextlost', event => {
          event.preventDefault();
          this.fallback();
          canvas.dispatchEvent(new CustomEvent('field-renderer-change'));
        }, { once: true });
      } catch (error) {
        console.warn('[conceptual.id] Using the canvas atmosphere:', error.message);
        this.fallback();
      }
    } else this.ctx = canvas.getContext('2d');
    this.canvas.dataset.renderer = this.mode;
  }

  fallback() {
    // A canvas cannot change context type. Replace only its decorative surface.
    const next = this.canvas.cloneNode(false);
    this.canvas.replaceWith(next);
    this.canvas = next;
    this.gl = null;
    this.ctx = next.getContext('2d');
    this.mode = 'canvas';
    next.dataset.renderer = this.mode;
    this.resize(this.width || 1, this.height || 1, this.dpr || 1);
  }

  resize(width, height, dpr) {
    this.volumeKey = null;
    this.width = width;
    this.height = height;
    this.dpr = dpr;
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.gl?.viewport(0, 0, this.canvas.width, this.canvas.height);
    this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    if(this.gl){
      const gl=this.gl;
      const factor=Math.min(1,(width<720?420:740)/Math.max(width,height));
      this.volumeWidth=Math.max(1,Math.round(width*factor));
      this.volumeHeight=Math.max(1,Math.round(height*factor));
      gl.bindTexture(gl.TEXTURE_2D,this.volumeTexture);
      gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,this.volumeWidth,this.volumeHeight,0,gl.RGBA,gl.UNSIGNED_BYTE,null);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
      gl.bindFramebuffer(gl.FRAMEBUFFER,this.framebuffer);
      gl.framebufferTexture2D(gl.FRAMEBUFFER,gl.COLOR_ATTACHMENT0,gl.TEXTURE_2D,this.volumeTexture,0);
      const complete=gl.checkFramebufferStatus(gl.FRAMEBUFFER)===gl.FRAMEBUFFER_COMPLETE;
      gl.bindFramebuffer(gl.FRAMEBUFFER,null);
      if(!complete)this.fallback();
    }
  }

  setScene(nodes, groups, activeIds = null) {
    this.volumeKey = null;
    this.nodes = nodes;
    this.groups = groups;
    this.activeIds = activeIds;
    const random = seeded('conceptual.id:volumetric-field-v1');
    const mobile = this.width < 720 || window.matchMedia('(pointer: coarse)').matches;
    const budget = Math.round((mobile ? 14000 : 32000) * this.quality);
    const points = [];
    const add = (x, y, z, color, size, alpha, soft = .7) => points.push(x,y,z,...color,size,alpha,soft,random() * 6.283);
    if (!nodes.length) {
      this.points = new Float32Array();
      this.count = 0;
      this.canvas.dataset.particles = '0';
      this.upload();
      return;
    }
    const center = {
      x: nodes.reduce((sum,n)=>sum+n.x,0) / nodes.length,
      y: nodes.reduce((sum,n)=>sum+n.y,0) / nodes.length
    };
    const minX = Math.min(...nodes.map(n=>n.x)), maxX = Math.max(...nodes.map(n=>n.x));
    const minY = Math.min(...nodes.map(n=>n.y)), maxY = Math.max(...nodes.map(n=>n.y));
    const spread = Math.max(.12, (maxX-minX+maxY-minY) / 4);
    this.disc=new Float32Array([center.x,center.y,spread*1.25]);
    this.clouds=new Float32Array(12*4);
    this.cloudColours=new Float32Array(12*3);
    groups.slice(0,12).forEach((group,index)=>{
      const members=nodes.filter(n=>group.ids.includes(n.id));
      if(!members.length)return;
      const x=members.reduce((sum,n)=>sum+n.x,0)/members.length;
      const y=members.reduce((sum,n)=>sum+n.y,0)/members.length;
      const radius=Math.max(.035,members.reduce((sum,n)=>sum+Math.hypot(n.x-x,n.y-y),0)/members.length*1.25);
      const active=activeIds?members.filter(n=>activeIds.has(n.id)).length/members.length:1;
      this.clouds.set([x,y,radius,.35+active*.65],index*4);
      this.cloudColours.set(rgb(group.color),index*3);
    });
    const background = activeIds ? .32 : 1;
    const colours = [[.29,.41,.72],[.43,.33,.64],[.46,.6,.72],[.62,.54,.43]];

    // A broad, tilted disc supplies depth between the actual topic clouds.
    // This dust has no records or hit targets and never contributes to counts.
    for (let i=0; i<budget*.38; i++) {
      const radius = Math.pow(random(), .58) * spread * 1.30;
      const arm = Math.floor(random()*4);
      const angle = arm * Math.PI / 2 + radius / spread * 5.5 + normal(random) * (.10+radius/spread*.18);
      const px = Math.cos(angle)*radius;
      const py = Math.sin(angle)*radius*.55;
      const theta = -.35;
      const x = center.x + px*Math.cos(theta)-py*Math.sin(theta);
      const y = center.y + px*Math.sin(theta)+py*Math.cos(theta);
      const z = normal(random)*(.22+radius*.16);
      const color = colours[(arm+Math.floor(random()*2))%colours.length];
      add(x,y,z,color, .85+random()*3.8, (.065+random()*.105)*background, .7);
    }

    // Topic clouds are shaped by their real members, rather than fixed orbits.
    for (const group of groups) {
      const members = nodes.filter(n=>group.ids.includes(n.id));
      if (!members.length) continue;
      const cr = seeded(group.key);
      const colour = rgb(group.color);
      const cx = members.reduce((sum,n)=>sum+n.x,0)/members.length;
      const cy = members.reduce((sum,n)=>sum+n.y,0)/members.length;
      const amount = Math.round(budget*.34*members.length/nodes.length);
      for (let i=0;i<amount;i++) {
        const member = members[i%members.length];
        const t = cr();
        const arc = Math.sin(t*Math.PI);
        const bend = ((group.seed%13)-6)*.003;
        const x = cx*(1-t)+member.x*t+(member.y-cy)*arc*.22+normal(cr)*.009;
        const y = cy*(1-t)+member.y*t-(member.x-cx)*arc*.22+bend*arc+normal(cr)*.006;
        const focus = activeIds && !activeIds.has(member.id) ? .24 : 1;
        add(x,y,normal(cr)*.4,colour,1.2+cr()*5, (.05+cr()*.14)*focus,.92);
      }
    }

    // Each contribution has its own small cloud. Colour follows the layer mode.
    const perNode = Math.max(35, Math.floor(budget*.28/nodes.length));
    for (const node of nodes) {
      const nr = seeded(node.id);
      const colour = rgb(node.color);
      const focus = activeIds && !activeIds.has(node.id) ? .25 : 1;
      for (let i=0;i<perNode;i++) {
        const r = Math.pow(nr(),1.8)*(.022+Math.min(node.degree,10)*.0013);
        const angle = nr()*6.283+r*260;
        const x = node.x+Math.cos(angle)*r;
        const y = node.y+Math.sin(angle)*r*.6;
        add(x,y,normal(nr)*.35,colour,.9+nr()*4.4,(.06+nr()*.24)*focus,.72);
      }
    }
    this.points = new Float32Array(points);
    this.count = points.length / 10;
    this.canvas.dataset.particles = String(this.count);
    this.upload();
  }

  upload() {
    if (!this.gl) return;
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER,this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER,this.points,gl.STATIC_DRAW);
  }

  draw({ renderer, time, pointer = {x:0,y:0} }) {
    const origin = renderer.framedGraphToViewport({x:0,y:0});
    const bx = renderer.framedGraphToViewport({x:1,y:0});
    const by = renderer.framedGraphToViewport({x:0,y:1});
    const axisX = {x:bx.x-origin.x,y:bx.y-origin.y};
    const axisY = {x:by.x-origin.x,y:by.y-origin.y};
    const scale = Math.min(2.8, Math.max(.65, Math.sqrt(.56/renderer.getCamera().getState().ratio)));
    if (this.gl) {
      const gl=this.gl,u=this.uniforms;
      gl.bindFramebuffer(gl.FRAMEBUFFER,null);
      gl.viewport(0,0,this.canvas.width,this.canvas.height);
      gl.clearColor(0,0,0,0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      if(this.count)this.drawVolume({origin,axisX,axisY,time,pointer});
      gl.useProgram(this.program);
      gl.bindBuffer(gl.ARRAY_BUFFER,this.buffer);
      const sizes=[3,3,4],offsets=[0,3,6];
      this.attributes.forEach((attribute,i)=>{
        gl.enableVertexAttribArray(attribute);
        gl.vertexAttribPointer(attribute,sizes[i],gl.FLOAT,false,40,offsets[i]*4);
      });
      gl.uniform2f(u.resolution,this.width,this.height);
      gl.uniform2f(u.origin,origin.x,origin.y);
      gl.uniform2f(u.axisX,axisX.x,axisX.y);
      gl.uniform2f(u.axisY,axisY.x,axisY.y);
      gl.uniform2f(u.pointer,pointer.x,pointer.y);
      gl.uniform1f(u.time,time);
      gl.uniform1f(u.dpr,this.dpr);
      gl.uniform1f(u.scale,scale);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE,gl.ONE);
      gl.disable(gl.DEPTH_TEST);
      gl.drawArrays(gl.POINTS,0,this.count||0);
    } else this.drawCanvas({origin,axisX,axisY,scale,time,pointer});
  }

  drawQuad(program) {
    const gl=this.gl;
    gl.useProgram(program);gl.bindBuffer(gl.ARRAY_BUFFER,this.quad);
    const location=gl.getAttribLocation(program,'a_quad');
    gl.enableVertexAttribArray(location);gl.vertexAttribPointer(location,2,gl.FLOAT,false,0,0);
    gl.drawArrays(gl.TRIANGLE_STRIP,0,4);
  }

  drawVolume({origin,axisX,axisY,time,pointer}) {
    const gl=this.gl,u=this.volumeUniforms;
    const determinant=axisX.x*axisY.y-axisY.x*axisX.y;
    if(!Number.isFinite(determinant)||Math.abs(determinant)<.001)return;
    gl.disable(gl.BLEND);gl.disable(gl.DEPTH_TEST);
    const key=[origin.x,origin.y,axisX.x,axisX.y,axisY.x,axisY.y].map(n=>n.toFixed(1)).join(':')+`:${Math.floor(time*5)}:${pointer.x.toFixed(1)}:${pointer.y.toFixed(1)}`;
    if(this.volumeKey!==key){
      gl.bindFramebuffer(gl.FRAMEBUFFER,this.framebuffer);
      gl.viewport(0,0,this.volumeWidth,this.volumeHeight);
      gl.useProgram(this.volumeProgram);
      gl.uniform2f(u.resolution,this.width,this.height);
      gl.uniform2f(u.origin,origin.x,origin.y);
      gl.uniformMatrix2fv(u.inverse,false,new Float32Array([axisY.y/determinant,-axisX.y/determinant,-axisY.x/determinant,axisX.x/determinant]));
      gl.uniform2f(u.pointer,pointer.x,pointer.y);
      gl.uniform3fv(u.disc,this.disc);
      gl.uniform4fv(u['clouds[0]'],this.clouds);
      gl.uniform3fv(u['colours[0]'],this.cloudColours);
      gl.uniform1f(u.time,time);gl.uniform1f(u.focus,this.activeIds?.size ? .35 : 1.0);
      this.drawQuad(this.volumeProgram);
      this.volumeKey=key;
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER,null);
    gl.viewport(0,0,this.canvas.width,this.canvas.height);
    gl.clearColor(0,0,0,0);gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.copyProgram);
    gl.activeTexture(gl.TEXTURE0);gl.bindTexture(gl.TEXTURE_2D,this.volumeTexture);
    gl.uniform1i(gl.getUniformLocation(this.copyProgram,'u_image'),0);
    this.drawQuad(this.copyProgram);
  }

  drawCanvas({origin,axisX,axisY,scale,time,pointer}) {
    const ctx=this.ctx;
    if(!ctx)return;
    ctx.clearRect(0,0,this.width,this.height);
    ctx.globalCompositeOperation='lighter';
    // A bounded sample keeps the non-WebGL atmosphere usable on older devices.
    const stride=Math.max(1,Math.ceil((this.count||0)/4500));
    for(let index=0;index<(this.count||0);index+=stride){
      const i=index*10,p=this.points;
      const px=p[i]+Math.sin(time*.025+p[i+9])*.0017;
      const py=p[i+1]+Math.cos(time*.019+p[i+9]*1.3)*.0017;
      const x=origin.x+axisX.x*px+axisY.x*py+pointer.x*p[i+2]*15;
      const y=origin.y+axisX.y*px+axisY.y*py+pointer.y*p[i+2]*15;
      if(x<-16||y<-16||x>this.width+16||y>this.height+16)continue;
      ctx.fillStyle=`rgba(${Math.round(p[i+3]*255)},${Math.round(p[i+4]*255)},${Math.round(p[i+5]*255)},${p[i+7]*.55})`;
      ctx.beginPath();ctx.arc(x,y,Math.max(.4,p[i+6]*scale*.4),0,Math.PI*2);ctx.fill();
    }
    ctx.globalCompositeOperation='source-over';
  }

  destroy() {
    if(this.gl){
      const gl=this.gl;
      gl.deleteBuffer(this.buffer);gl.deleteBuffer(this.quad);
      gl.deleteProgram(this.program);gl.deleteProgram(this.volumeProgram);gl.deleteProgram(this.copyProgram);
      gl.deleteTexture(this.volumeTexture);gl.deleteFramebuffer(this.framebuffer);
    }
  }
}
